import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Stamp, ArrowRight, ExternalLink } from "lucide-react";

const KOMMUNER = ["Vårgårda", "Herrljunga", "Vara", "Alingsås"];
export const LANSSTYRELSEN_NP_URL =
    "https://www.lansstyrelsen.se/vastra-gotaland/samhalle/tillstand-for-att-utova-verksamhet/notarius-publicus.html";

export default function NotariusBanner() {
    return (
        <section
            data-testid="notarius-banner"
            className="relative py-24 bg-[#0A0A0B] border-t border-white/5 overflow-hidden"
        >
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-20 right-0 w-[480px] h-[480px] bg-[#C4A977]/[0.05] rounded-full blur-3xl" />
            </div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-7"
                    >
                        <div className="flex items-center gap-3">
                            <span className="w-12 h-12 border border-[#C4A977]/40 grid place-items-center text-[#C4A977]">
                                <Stamp size={18} />
                            </span>
                            <p className="overline">Officiell myndighetsroll</p>
                        </div>
                        <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            Notarius Publicus —{" "}
                            <span className="italic text-[#C4A977]">
                                apostille, vidimering & intyg
                            </span>
                            .
                        </h2>
                        <p className="mt-6 text-[#A1A1AA] text-base lg:text-lg leading-relaxed max-w-2xl">
                            Daniel Madsen är av Länsstyrelsen förordnad notarius
                            publicus för Vårgårda, Herrljunga, Vara och Alingsås
                            kommun — med rätt att verka i hela riket. När dina handlingar
                            ska gälla utomlands eller formellt bestyrkas är det
                            oss du vänder dig till.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link
                                to="/tjanster#notarius-publicus"
                                data-testid="notarius-cta-primary"
                                className="inline-flex items-center gap-3 px-7 py-4 bg-[#C4A977] text-black hover:bg-[#DBC08F] rounded-sm text-sm font-medium tracking-wide transition-colors group"
                            >
                                Boka Notarius Publicus
                                <ArrowRight
                                    size={14}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </Link>
                            <Link
                                to="/kontakt"
                                data-testid="notarius-cta-secondary"
                                className="inline-flex items-center gap-3 px-7 py-4 border border-white/15 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm text-sm tracking-wide transition-colors"
                            >
                                Kontakta oss
                            </Link>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="lg:col-span-5"
                    >
                        <div className="bg-[#111113] border border-[#C4A977]/25 p-8 rounded-sm">
                            <p className="overline">Förordnande</p>
                            <p className="mt-4 font-serif text-2xl text-[#F4F4F5] leading-snug">
                                Kommuner där Daniel Madsen är förordnad:
                            </p>
                            <ul className="mt-5 space-y-2">
                                {KOMMUNER.map((k) => (
                                    <li
                                        key={k}
                                        data-testid={`notarius-kommun-${k.toLowerCase()}`}
                                        className="flex items-center gap-3 text-[#F4F4F5]/90"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#C4A977] rounded-full" />
                                        {k} kommun
                                    </li>
                                ))}
                            </ul>
                            <div className="hairline my-7" />
                            <p className="text-sm text-[#A1A1AA] leading-relaxed">
                                Förordnandet ger rätt att utföra förrättningar i
                                hela Sverige — oavsett var du eller dina
                                handlingar befinner sig. Vi tar emot bokningar
                                vid samtliga sex kontor samt på distans.
                            </p>
                            <a
                                href={LANSSTYRELSEN_NP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="notarius-lansstyrelsen-link"
                                className="mt-5 inline-flex items-center gap-2 text-sm text-[#C4A977] hover:text-[#DBC08F] transition-colors group"
                            >
                                Läs mer om notarius publicus hos Länsstyrelsen
                                <ExternalLink
                                    size={13}
                                    className="transition-transform duration-300 group-hover:translate-x-0.5"
                                />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
